import { apiFetch } from '@/lib/api/http';

export type MeetQueueStatus = 'auto' | 'needs_review' | 'resolved';

export const MEET_QUEUE_STATUS_LABELS: Record<MeetQueueStatus, string> = {
  auto: '自動確定',
  needs_review: '要確認',
  resolved: '解決済み',
};

export interface MeetAttendanceLog {
  id: string;
  event_id: string;
  email: string;
  display_name: string | null;
  join_at: string | null;
  leave_at: string | null;
  duration_seconds: number | null;
  matched_member_id: string | null;
  matched_member_name: string | null;
  queue_status: MeetQueueStatus;
  resolved_by: string | null;
  /** この解決で会員の「Meet用メール」に学習させたメール（D-070） */
  learned_meet_email: string | null;
}

export function fetchMeetQueue(
  eventId: string,
  status?: MeetQueueStatus,
): Promise<MeetAttendanceLog[]> {
  const query = status ? `?status=${status}` : '';
  return apiFetch<MeetAttendanceLog[]>(`/events/${eventId}/meet-queue${query}`);
}

export function resolveMeetQueueItem(
  eventId: string,
  logId: string,
  memberId: string | null,
): Promise<MeetAttendanceLog> {
  return apiFetch<MeetAttendanceLog>(`/events/${eventId}/meet-queue/${logId}/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ member_id: memberId }),
  });
}

// --- Driveファイル（文字起こし・資料のリンク / implementation-spec §8・D-064） -------

export type DriveFileKind = 'transcript' | 'material';

export const DRIVE_FILE_KIND_LABELS: Record<DriveFileKind, string> = {
  transcript: '文字起こし',
  material: '資料',
};

export type DriveSyncStatus = 'unknown' | 'synced' | 'missing' | 'error';

export const DRIVE_SYNC_STATUS_LABELS: Record<DriveSyncStatus, string> = {
  unknown: '未確認',
  synced: '確認済み',
  missing: '見つからない',
  error: 'エラー',
};

export interface DriveFile {
  id: string;
  event_id: string;
  kind: DriveFileKind;
  title: string | null;
  url: string;
  sync_status: DriveSyncStatus;
  last_synced_at: string | null;
}

export function fetchDriveFiles(eventId: string): Promise<DriveFile[]> {
  return apiFetch<DriveFile[]>(`/events/${eventId}/drive-files`);
}

export function addDriveFile(
  eventId: string,
  payload: { kind: DriveFileKind; title: string | null; url: string },
): Promise<DriveFile> {
  return apiFetch<DriveFile>(`/events/${eventId}/drive-files`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function setDriveFileSyncStatus(
  eventId: string,
  driveFileId: string,
  syncStatus: DriveSyncStatus,
): Promise<DriveFile> {
  return apiFetch<DriveFile>(`/events/${eventId}/drive-files/${driveFileId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sync_status: syncStatus }),
  });
}

export function deleteDriveFile(eventId: string, driveFileId: string): Promise<void> {
  return apiFetch<void>(`/events/${eventId}/drive-files/${driveFileId}`, { method: 'DELETE' });
}

/** 突合状況のサマリ（D-070）。事務局が「あと何を登録すれば自動化が進むか」を判断する */
export interface MeetMatchSummary {
  total: number;
  auto_matched: number;
  needs_review: number;
  resolved: number;
  members_without_meet_email: number;
}

export function fetchMeetMatchSummary(eventId: string): Promise<MeetMatchSummary> {
  return apiFetch<MeetMatchSummary>(`/events/${eventId}/meet-queue/summary`);
}
